import React, { useEffect, useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  CAlert,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CContainer,
  CSpinner,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CCol,
  CRow,
} from "@coreui/react";
import { CChartBar } from "@coreui/react-chartjs";
import settingsStore from "../../store/SettingsStore";
import compareStore from "../../store/CompareStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import { fmtInt } from "../../utils/chartHelpers";
import { formatYmd, quickRangeDates } from "../../utils/dateRange";
import { logReportView } from "../../api/reportLogApi";
import InsightsPanel from "../Insights/InsightsPanel";
import { buildCompareInsights } from "../../utils/insightsEngine";

function fmtMetric(val, unit) {
  if (unit === "%") return `${Number(val).toFixed(1)}%`;
  if (unit === "₽") return `${fmtInt(val)} ₽`;
  return fmtInt(val);
}

const ComparePage = observer(() => {
  const [selectedServer, setSelectedServer] = useState("");
  const [fromA, setFromA] = useState("");
  const [toA, setToA] = useState("");
  const [fromB, setFromB] = useState("");
  const [toB, setToB] = useState("");
  const [quickA, setQuickA] = useState("");
  const [quickB, setQuickB] = useState("");

  const load = useCallback(() => {
    if (!selectedServer || !fromA || !toA || !fromB || !toB) return;
    compareStore.fetchCompare(selectedServer, fromA, toA, fromB, toB);
  }, [selectedServer, fromA, toA, fromB, toB]);

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const thisWeek = quickRangeDates("week");
      const today = new Date();
      const prevWeekEnd = new Date(today.getTime() - 8 * 24 * 60 * 60 * 1000);
      const prevWeekStart = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
      setFromA(thisWeek.from);
      setToA(thisWeek.to);
      setFromB(formatYmd(prevWeekStart));
      setToB(formatYmd(prevWeekEnd));
      logReportView({
        event: "report_view",
        reportKey: "compare",
        reportTitle: "Сравнение периодов",
        userId,
        path: "/compare",
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    load();
  }, [load]);

  const applyThisVsLastWeek = () => {
    const thisWeek = quickRangeDates("week");
    const today = new Date();
    const prevWeekEnd = new Date(today.getTime() - 8 * 24 * 60 * 60 * 1000);
    const prevWeekStart = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
    setFromA(thisWeek.from);
    setToA(thisWeek.to);
    setFromB(formatYmd(prevWeekStart));
    setToB(formatYmd(prevWeekEnd));
    setQuickA("week");
    setQuickB("");
  };

  const revenueRow = compareStore.comparison.find((c) => c.key === "revenue");

  const compareInsights = useMemo(() => {
    if (compareStore.isLoading || !compareStore.comparison.length) return [];
    return buildCompareInsights(compareStore.comparison);
  }, [compareStore.isLoading, compareStore.comparison]);

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Сравнение периодов</h4>
      <ServerSelect
        servers={settingsStore.servers}
        value={selectedServer}
        onChange={(e) => setSelectedServer(e.target.value)}
      />
      <CButtonGroup className="mb-3">
        <CButton color="primary" variant="outline" onClick={applyThisVsLastWeek}>
          Эта неделя vs прошлая
        </CButton>
        <CButton color="primary" onClick={load} disabled={compareStore.isLoading}>
          Сравнить
        </CButton>
      </CButtonGroup>
      <CRow className="g-4">
        <CCol lg={6}>
          <CCard>
            <CCardHeader>Период A</CCardHeader>
            <CCardBody>
              <PeriodFilters
                fromDate={fromA}
                toDate={toA}
                onFromChange={setFromA}
                onToChange={setToA}
                onRefresh={load}
                quickRange={quickA}
                onQuickRangeChange={setQuickA}
                disabled={compareStore.isLoading}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard>
            <CCardHeader>Период B</CCardHeader>
            <CCardBody>
              <PeriodFilters
                fromDate={fromB}
                toDate={toB}
                onFromChange={setFromB}
                onToChange={setToB}
                onRefresh={load}
                quickRange={quickB}
                onQuickRangeChange={setQuickB}
                disabled={compareStore.isLoading}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      {compareStore.errorMessage && (
        <CAlert color="danger" className="mt-3">
          {compareStore.errorMessage}
        </CAlert>
      )}
      {compareStore.isLoading && (
        <div className="text-center py-4">
          <CSpinner />
        </div>
      )}
      {!compareStore.isLoading && compareStore.comparison.length > 0 && (
        <>
          <InsightsPanel insights={compareInsights} title="Выводы по сравнению" />
          {revenueRow && (
            <CCard className="mt-4">
              <CCardBody>
                <CChartBar
                  data={{
                    labels: ["Выручка"],
                    datasets: [
                      {
                        label: `A (${fromA} — ${toA})`,
                        backgroundColor: "#36A2EB",
                        data: [revenueRow.valA],
                      },
                      {
                        label: `B (${fromB} — ${toB})`,
                        backgroundColor: "#FF6384",
                        data: [revenueRow.valB],
                      },
                    ],
                  }}
                />
              </CCardBody>
            </CCard>
          )}
          <CTable hover responsive className="mt-4">
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Метрика</CTableHeaderCell>
                <CTableHeaderCell>Период A</CTableHeaderCell>
                <CTableHeaderCell>Период B</CTableHeaderCell>
                <CTableHeaderCell>Δ</CTableHeaderCell>
                <CTableHeaderCell>Δ %</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {compareStore.comparison.map((row) => {
                const positiveGood = row.key !== "returns" && row.key !== "foodCost";
                const deltaColor =
                  row.delta == null
                    ? ""
                    : row.delta > 0
                      ? positiveGood
                        ? "text-success"
                        : "text-danger"
                      : positiveGood
                        ? "text-danger"
                        : "text-success";
                return (
                  <CTableRow key={row.key}>
                    <CTableDataCell>{row.label}</CTableDataCell>
                    <CTableDataCell>{fmtMetric(row.valA, row.unit)}</CTableDataCell>
                    <CTableDataCell>{fmtMetric(row.valB, row.unit)}</CTableDataCell>
                    <CTableDataCell className={deltaColor}>
                      {row.unit === "%"
                        ? `${row.delta >= 0 ? "+" : ""}${row.delta.toFixed(1)} п.п.`
                        : `${row.delta >= 0 ? "+" : ""}${fmtMetric(row.delta, row.unit)}`}
                    </CTableDataCell>
                    <CTableDataCell className={deltaColor}>
                      {row.deltaPct != null ? `${row.deltaPct >= 0 ? "+" : ""}${row.deltaPct.toFixed(1)}%` : "—"}
                    </CTableDataCell>
                  </CTableRow>
                );
              })}
            </CTableBody>
          </CTable>
        </>
      )}
    </CContainer>
  );
});

export default ComparePage;
